/**
 * 站内搜索索引。
 *
 * 站点是纯静态的，因此不引入任何搜索服务：页面、文章与产品条目在构建期
 * 就从同一批内容模块里汇总成一张小表，运行期只在浏览器内存里匹配。
 * 全站条目数在百条以内，不需要分词或倒排索引。
 */
import { PRODUCTS, PRODUCT_CATEGORIES, BADGE_LABELS } from '@/content/products';
import { NEWS_META, ROUTES } from '@/content/site';
import { rankItems } from './agent-score';

export type SearchKind = '页面' | '产品' | '动态';

export interface SearchEntry {
  id: string;
  kind: SearchKind;
  title: string;
  description: string;
  href: string;
  /** 参与匹配的整段文本（标题 + 描述 + 关键词），已转小写。 */
  haystack: string;
}

function entry(
  id: string,
  kind: SearchKind,
  title: string,
  description: string,
  href: string,
  extra: readonly string[] = [],
): SearchEntry {
  return {
    id,
    kind,
    title,
    description,
    href,
    haystack: [title, description, ...extra].join(' ').toLowerCase(),
  };
}

function buildIndex(): SearchEntry[] {
  const pages = ROUTES.map((route) => entry(route.path, '页面', route.label, route.description, route.path));

  const articles = NEWS_META.map((article) =>
    entry(article.slug, '动态', article.title, article.excerpt, `/news/${article.slug}`, article.tags),
  );

  const categoryName = new Map(PRODUCT_CATEGORIES.map((category) => [category.id, category.name]));

  const products = PRODUCTS.map((product) =>
    entry(product.id, '产品', product.name, product.tagline, `/products#${product.id}`, [
      product.note ?? '',
      categoryName.get(product.categoryId) ?? '',
      product.badge ? BADGE_LABELS[product.badge] : '',
    ]),
  );

  return [...pages, ...products, ...articles];
}

export const SEARCH_INDEX: readonly SearchEntry[] = buildIndex();

/**
 * 按空格拆词，全部命中才算匹配；标题命中权重高于其余字段。
 * 中文查询通常只有一个词，空格拆词对中英混输同样合适。
 *
 * 打分实现在 `agent-score.ts`，与 `/api/agent/search` 共用，避免两处结果不一致。
 */
export function search(rawQuery: string, limit = 8): SearchEntry[] {
  return rankItems(SEARCH_INDEX, rawQuery, limit).map((row) => row.item);
}
